if (!customElements.get('locale-suggestion-banner')) {
  customElements.define(
    'locale-suggestion-banner',
    class LocaleSuggestionBanner extends HTMLElement {
      constructor() {
        super();
        this.cookieName = 'chaplin_locale_choice';
        this.cookieMaxAgeDays = 365;
        this.pendingToastKey = 'chaplinLocalePendingToast';
      }

      connectedCallback() {
        this.currentCountry = this.getAttribute('data-current-country') || '';
        this.currentLanguage = this.getAttribute('data-current-language') || '';

        // The manual language/country switcher is always available, regardless of
        // auto-redirect/cookie state - Frank explicitly wanted a manual option kept.
        this.mountManualControl();

        // If we just auto-redirected the visitor here, show the confirmation toast and stop -
        // don't re-run detection on the very page we redirected to.
        if (this.showPendingToastIfAny()) return;

        // Visitor already made an explicit choice (auto-redirect accepted, manual switch, or
        // "back to English") - respect it silently forever, no more redirects/toasts.
        if (this.getCookie(this.cookieName)) return;

        // Never redirect bots/crawlers - behave exactly as if no suggestion was detected.
        if (this.isBot()) return;

        this.fetchSuggestion();
      }

      // ---------- Cookie helpers ----------

      setCookie(name, value, days) {
        try {
          const maxAge = Math.round(days * 24 * 60 * 60);
          document.cookie =
            name + '=' + encodeURIComponent(value) + '; Max-Age=' + maxAge + '; Path=/; SameSite=Lax';
        } catch (e) {
          // Cookies unavailable - fail silently, worst case the flow may run again.
        }
      }

      getCookie(name) {
        try {
          const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
          const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
          return match ? decodeURIComponent(match[1]) : null;
        } catch (e) {
          return null;
        }
      }

      // ---------- Bot detection ----------

      isBot() {
        try {
          if (navigator.webdriver) return true;
          const ua = navigator.userAgent || '';
          const botPattern =
            /googlebot|bingbot|yandex(bot)?|baiduspider|duckduckbot|facebookexternalhit|twitterbot|linkedinbot|slackbot|ahrefsbot|semrushbot|mj12bot|dotbot|petalbot|bytespider|applebot|ia_archiver|discordbot|telegrambot|whatsapp|bot|spider|crawler|crawling/i;
          return botPattern.test(ua);
        } catch (e) {
          return false;
        }
      }

      // ---------- Manual "choose your own" control (always available, persistent) ----------

      mountManualControl() {
        const template = this.querySelector('[data-locale-manual-template]');
        if (!template) return;

        const fragment = template.content.cloneNode(true);
        const form = fragment.querySelector('form[action="/localization"]');
        if (form) {
          form.addEventListener('submit', () => {
            const country = form.querySelector('[name="country_code"]');
            const language = form.querySelector('[name="language_code"]');
            this.setCookie(
              this.cookieName,
              'manual:' +
                (language ? language.value : this.currentLanguage) +
                '-' +
                (country ? country.value : this.currentCountry),
              this.cookieMaxAgeDays
            );
          });
        }

        this.appendChild(fragment);
      }

      // ---------- Detection (reused from the original suggestion banner) ----------

      format(str, vars) {
        return str.replace(/%\{(\w+)\}/g, (match, key) => (vars && key in vars ? vars[key] : match));
      }

      async fetchSuggestion() {
        try {
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

          const url =
            root.replace(/\/$/, '') +
            '/browsing_context_suggestions.json' +
            '?country[enabled]=true' +
            '&country[exclude]=' + encodeURIComponent(this.currentCountry) +
            '&language[enabled]=true' +
            '&language[exclude]=' + encodeURIComponent(this.currentLanguage);

          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!response.ok) return;

          const data = await response.json();
          const suggestion = data && data.suggestions && data.suggestions[0];
          if (!suggestion || !suggestion.parts) return;

          // Require reasonable confidence before acting on it.
          if (typeof suggestion.confidence === 'number' && suggestion.confidence < 0.3) return;

          const suggestedCountry = suggestion.parts.country;
          const suggestedLanguage = suggestion.parts.language;

          if (!suggestedCountry && !suggestedLanguage) return;

          const countryCode = (suggestedCountry && suggestedCountry.handle) || this.currentCountry;
          const languageCode = (suggestedLanguage && suggestedLanguage.handle) || this.currentLanguage;
          const languageName = (suggestedLanguage && suggestedLanguage.name) || languageCode;

          // No redirect loop: nothing to do if the suggestion matches what's already active.
          if (countryCode === this.currentCountry && languageCode === this.currentLanguage) return;

          this.maybeAutoRedirect({ countryCode, languageCode, languageName });
        } catch (e) {
          // Network hiccup or endpoint unavailable - never block the storefront.
        }
      }

      // ---------- Auto-redirect ----------

      maybeAutoRedirect({ countryCode, languageCode, languageName }) {
        // Only a language mismatch represents an actual "different storefront" (/de/, /es/,
        // /fr/, /pl/). A country-only mismatch (same language, different currency/market) is not
        // part of this auto-redirect flow.
        if (!languageCode || languageCode === this.currentLanguage) return;

        const payload = {
          prevLanguage: this.currentLanguage,
          prevCountry: this.currentCountry,
          newLanguage: languageCode,
          newLanguageName: languageName,
          newCountry: countryCode || this.currentCountry,
        };

        try {
          window.sessionStorage.setItem(this.pendingToastKey, JSON.stringify(payload));
        } catch (e) {
          // sessionStorage unavailable - the redirect will still happen, we just won't be able
          // to show the toast afterwards.
        }

        // Lock in the choice now (before navigating) so this only ever fires once, and so a
        // visitor who later navigates back to English on purpose is never redirected again.
        this.setCookie(this.cookieName, 'auto:' + languageCode + '-' + payload.newCountry, this.cookieMaxAgeDays);

        this.switchLocale(payload.newCountry, languageCode);
      }

      // ---------- Toast shown right after an auto-redirect ----------

      showPendingToastIfAny() {
        let raw = null;
        try {
          raw = window.sessionStorage.getItem(this.pendingToastKey);
        } catch (e) {
          return false;
        }
        if (!raw) return false;

        try {
          window.sessionStorage.removeItem(this.pendingToastKey);
        } catch (e) {
          // ignore
        }

        let payload;
        try {
          payload = JSON.parse(raw);
        } catch (e) {
          return false;
        }
        if (!payload) return false;

        this.renderToast(payload);
        return true;
      }

      renderToast(payload) {
        const template = this.querySelector('[data-locale-toast-template]');
        if (!template) return;

        const fragment = template.content.cloneNode(true);

        const backBtn = fragment.querySelector('[data-locale-toast-back]');
        if (backBtn) {
          backBtn.addEventListener('click', () => {
            this.setCookie(
              this.cookieName,
              'back:' + (payload.prevLanguage || 'en') + '-' + (payload.prevCountry || ''),
              this.cookieMaxAgeDays
            );
            this.switchLocale(payload.prevCountry || this.currentCountry, payload.prevLanguage || 'en');
          });
        }

        const dismissBtn = fragment.querySelector('[data-locale-toast-dismiss]');
        const toastEl = fragment.querySelector('.locale-redirect-toast');
        if (dismissBtn && toastEl) {
          dismissBtn.addEventListener('click', () => {
            toastEl.remove();
          });
        }

        this.appendChild(fragment);
      }

      // ---------- Shared navigation helper (drives both the auto-redirect and the toast's
      // "back to English" action; the manual control uses its own native form submit) ----------

      switchLocale(countryCode, languageCode) {
        const returnTo = window.location.pathname + window.location.search;
        const form = document.createElement('form');
        form.action = '/localization';
        form.method = 'post';
        form.hidden = true;

        const fields = { _method: 'PUT', country_code: countryCode, language_code: languageCode, return_to: returnTo };
        Object.keys(fields).forEach((name) => {
          const input = document.createElement('input');
          input.name = name;
          input.value = fields[name];
          form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
      }
    },
  );
}
